import { createContext, useContext, useState, useEffect, PropsWithChildren } from "react";

import { redirectToLogin, UserDto, getLoggedInUser  } from './api/basicApi.ts';
import { AppConfigurationContext } from './appConfigurationProvider.tsx';
import { LoadingBanner, LoadingState } from './components/loadingBanner.tsx';


// This is a "context provider" React component for the user details object

enum CurrentUserLoadingState { NotTriggered, Pending, Succeeded, Failed };

// The structure we are providing to components in the hierarchy below the provider
interface CurrentUserContextDto {
  userDetails: UserDto | null;
  name: string;
  loggedIn: boolean;
  loadingState: CurrentUserLoadingState;
}

const CurrentUserContext = createContext<CurrentUserContextDto>({
    userDetails: null,
    loggedIn: false,
    name: "",
    loadingState: CurrentUserLoadingState.NotTriggered,
  });

const CurrentUserProvider: React.FC<PropsWithChildren> = (props) => {
  const appConfig = useContext(AppConfigurationContext);

  const [loggedIn, setLoggedIn] = useState<boolean>(false);
  const [loadingState, setLoadingState] = useState<CurrentUserLoadingState>(CurrentUserLoadingState.NotTriggered);
  const [userDetailsObject, setUserDetailsObject] = useState<UserDto | null>(null);
  const [userName, setUserName] = useState<string>("");
  const [loadingBannerState, setLoadingBannerState] = useState<LoadingState>(LoadingState.Loading);
  const [loadingMessage, setLoadingMessage] = useState("Fetching user info...");


  useEffect(() => {
    appConfig.log('CurrentUserProvider mounted');
    setLoadingState(CurrentUserLoadingState.Pending);
    return () => {
      appConfig.log('CurrentUserProvider unmounted');
    };
  }, []);


  useEffect(() => {

    const fetchData = async () => {
      try {
        const result = await getLoggedInUser();
        if (result.success) {
          const details = result.response!;
          appConfig.log('User details:', details);
          setUserDetailsObject(details);
          if (details.first_name && details.last_name) {
            setUserName(details.first_name + " " + details.last_name);
          } else {
            setUserName(details.username);
          }
          setLoggedIn(true);
          setLoadingState(CurrentUserLoadingState.Succeeded);
          return;
        }
      } catch (error) {
        appConfig.log('Error fetching user details:', error);
      }
      // Anything other than a successful fetch causes a redirect to the login page.
      setLoadingState(CurrentUserLoadingState.Failed);
      setLoadingBannerState(LoadingState.Failure);
      setLoadingMessage("No user details found. Redirecting you to login page...");
      setLoggedIn(false);
      redirectToLogin();
    };

    // Call fetchData when the loadingState changes
    if (loadingState == CurrentUserLoadingState.Pending) {
      fetchData();
    }
  }, [loadingState]);


  // If we're in any loading state other than success,
  // display a loading banner instead of the content.
  if (loadingState != CurrentUserLoadingState.Succeeded) {
    return (<LoadingBanner state={loadingBannerState} message={loadingMessage}></LoadingBanner>)
  }


  return (
    <CurrentUserContext.Provider value={{
        userDetails: userDetailsObject,
        loggedIn: loggedIn,
        name: userName,
        loadingState: loadingState
    }}>
    {props.children}
    </CurrentUserContext.Provider>
  )
}

export { CurrentUserContext, CurrentUserProvider, CurrentUserLoadingState }